import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import type {
    InventoryAdditionalFilters,
    InventoryStatusFilter,
} from '../../types/inventory';
import type {
    InventoryLiveSummaryRequest,
    InventoryLiveSummaryResponse,
} from '../../types/reporting';

/** Local civil YYYY-MM-DD (avoids UTC day-shift from toISOString). */
const toLocalYmd = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const dateFilterKey = (value: Date | string): string =>
    value instanceof Date ? toLocalYmd(value) : String(value).slice(0, 10);

const serializeAdditionalFilters = (filters: InventoryAdditionalFilters) => {
    const { dateAddedRange, ...rest } = filters;
    return JSON.stringify({
        ...rest,
        dateAddedRange: dateAddedRange
            ? {
                  start: dateFilterKey(dateAddedRange.start),
                  end: dateFilterKey(dateAddedRange.end),
              }
            : undefined,
    });
};

const toApiAdditionalFilters = (
    filters: InventoryAdditionalFilters | undefined,
): InventoryLiveSummaryRequest['additionalFilters'] => {
    if (!filters || Object.keys(filters).length === 0) return undefined;
    const { dateAddedRange, ...rest } = filters;
    return {
        ...rest,
        dateAddedRange: dateAddedRange
            ? {
                  start: dateFilterKey(dateAddedRange.start),
                  end: dateFilterKey(dateAddedRange.end),
              }
            : undefined,
    };
};

const getInventorySummary = async (
    sessionId: string,
    input: InventoryLiveSummaryRequest,
): Promise<InventoryLiveSummaryResponse> => {
    const { data } = await axios.post<InventoryLiveSummaryResponse>(
        `${API_URL}/inventory/summary`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useInventorySummary = (
    statusFilter: InventoryStatusFilter = 'active',
    locationIds: Array<string> | 'all',
    search: string = '',
    additionalFilters: InventoryAdditionalFilters = {},
    enabled = true,
) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: [
            '/inventory/summary',
            sessionContext?.organizationId ?? '',
            statusFilter,
            locationIds === 'all' ? 'all' : locationIds.join(','),
            search,
            serializeAdditionalFilters(additionalFilters),
        ],
        queryFn: () => {
            const feedLocationIds =
                locationIds === 'all' || locationIds.length === 0 ? 'all' : locationIds;

            return getInventorySummary(sessionContext?.sessionId ?? '', {
                statusFilter,
                locationIds: feedLocationIds,
                search: search || undefined,
                additionalFilters: toApiAdditionalFilters(additionalFilters),
            });
        },
        enabled: enabled && !!sessionContext?.sessionId,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchInterval: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
